import { InjectionToken } from '@angular/core';

/**
 * Injection token for IPingService.
 */
export const PING_SERVICE = new InjectionToken<IPingService>('PING_SERVICE');

/**
 * Abstract interface for network diagnostics.
 * Allows swapping implementations (device direct vs cloud tunnel), the same seam the
 * file explorer uses via FILE_EXPLORER_SERVICE.
 */
export interface IPingService {
    networkPing(request: PingRequest): Promise<PingResult>;
}

export interface PingRequest {
    /** Target hostname or IP address. */
    host: string;
    /** Number of ICMP echo requests (clamped to 1-10 by the device). */
    count?: number;
    /** Per-packet timeout in milliseconds (clamped to 200-5000 by the device). */
    timeoutMs?: number;
    /** Optional single TCP port; shorthand for a one-port scan. */
    port?: number;
    /** Optional port list/range specification, e.g. '22,80,443,8000-8010'. */
    ports?: string;
    /** Per-port connect timeout for the scan phase. */
    portTimeoutMs?: number;
    /** Whether to perform forward and reverse DNS resolution. */
    resolveDns?: boolean;
}

export interface PingAttempt {
    sequence: number;
    success: boolean;
    /** IPStatus name, e.g. 'Success' or 'TimedOut'. */
    status: string;
    roundtripMs: number;
    address: string;
    ttl?: number | null;
}

export interface PingSummary {
    sent: number;
    received: number;
    lost: number;
    lossPercent: number;
    minMs?: number | null;
    avgMs?: number | null;
    maxMs?: number | null;
}

export interface PingResultDetail {
    attempts: PingAttempt[];
    summary: PingSummary;
}

export interface DnsLookupResult {
    success: boolean;
    addresses: string[];
    reverseHostName?: string | null;
    error?: string | null;
}

export interface TcpProbeResult {
    port: number;
    success: boolean;
    elapsedMs: number;
    error?: string | null;
}

export interface PortScanResult {
    /** How many ports were actually probed. */
    scanned: number;
    /** How many accepted a connection. */
    open: number;
    /** Per-port outcome, ascending. */
    results: TcpProbeResult[];
}

export interface PingResult {
    host: string;
    resolvedAddress?: string | null;
    /** True when the target replied to ICMP, or an explicitly requested port accepted a connection. */
    success: boolean;
    ping: PingResultDetail;
    dns?: DnsLookupResult | null;
    /** Present only when ports were requested. */
    ports?: PortScanResult | null;
    /** Console output pre-rendered by the device so every front end shows identical text. */
    lines: string[];
    startedUtc: string;
    durationMs: number;
    /** True when the device's wall-clock budget expired and the results are partial. */
    timedOut: boolean;
    error?: string | null;
}

/** Bounds mirrored from the device so the form can constrain input before sending. */
export const PING_LIMITS = {
    defaultCount: 4,
    minCount: 1,
    maxCount: 10,
    defaultTimeoutMs: 2000,
    minTimeoutMs: 200,
    maxTimeoutMs: 5000,
    /**
     * Ceiling on count x timeout. A longer per-packet timeout buys fewer packets, so every
     * accepted request completes inside the device's ICMP allowance.
     */
    maxTotalPingMs: 20000,

    defaultPortTimeoutMs: 1000,
    minPortTimeoutMs: 200,
    maxPortTimeoutMs: 3000,
    /** Hard ceiling on ports per scan. */
    maxPorts: 256,
    /** How many connects the device runs at once. */
    scanConcurrency: 16,
    /** Ceiling on the device's scan phase. */
    maxScanMs: 20000
} as const;

/**
 * The most ports that fit in the device's scan allowance at the given per-port timeout.
 * Mirrors NetworkPingValidator.MaxPortsFor so the form can warn before the API refuses.
 */
export function maxPortsFor(portTimeoutMs: number): number {
    if (!Number.isFinite(portTimeoutMs) || portTimeoutMs <= 0) return PING_LIMITS.maxPorts;
    const rounds = Math.floor(PING_LIMITS.maxScanMs / portTimeoutMs);
    return Math.min(PING_LIMITS.maxPorts, Math.max(1, rounds * PING_LIMITS.scanConcurrency));
}

/**
 * Counts the ports a spec resolves to, without expanding huge ranges.
 * Returns null when the spec is malformed, so the form can flag it before sending.
 */
export function countPortsInSpec(spec: string): number | null {
    const trimmed = (spec ?? '').trim();
    if (!trimmed) return 0;

    const seen = new Set<number>();
    for (const rawPart of trimmed.split(',')) {
        const part = rawPart.trim();
        if (!part) continue;

        const dash = part.indexOf('-');
        if (dash < 0) {
            const single = Number(part);
            if (!Number.isInteger(single) || single < 1 || single > 65535) return null;
            seen.add(single);
            continue;
        }

        const from = Number(part.slice(0, dash).trim());
        const to = Number(part.slice(dash + 1).trim());
        if (!Number.isInteger(from) || !Number.isInteger(to)) return null;
        if (from < 1 || from > 65535 || to < 1 || to > 65535) return null;
        if (to < from) return null;
        if (to - from + 1 > PING_LIMITS.maxPorts * 4) return null;
        for (let p = from; p <= to; p++) seen.add(p);
    }
    return seen.size;
}

/**
 * TCP ports worth probing on AV and control equipment. UDP-only services (Q-SYS discovery
 * 2467-2470, mDNS 5353) are deliberately excluded — a TCP connect cannot reach them, so they
 * would report a false negative on every device.
 */
export const COMMON_AV_PORTS: ReadonlyArray<{ port: number; service: string }> = [
    { port: 22, service: 'SSH / SFTP' },
    { port: 23, service: 'Telnet' },
    { port: 80, service: 'HTTP' },
    { port: 443, service: 'HTTPS' },
    { port: 502, service: 'Modbus TCP' },
    { port: 1710, service: 'Q-SYS QRC' },
    { port: 2001, service: 'Extron SIS' },
    { port: 3389, service: 'RDP' },
    { port: 4352, service: 'PJLink' },
    { port: 5000, service: 'Device REST API' },
    { port: 5900, service: 'VNC' },
    { port: 8080, service: 'HTTP alt / embedded web server' },
    { port: 8443, service: 'HTTPS alt' },
    { port: 9090, service: 'Embedded HTTPS' },
    { port: 41794, service: 'Crestron CIP' },
    { port: 41795, service: 'Crestron CTP console' },
    { port: 41796, service: 'Crestron secure CIP' },
    { port: 41797, service: 'Crestron secure CTP' }
];

/** The AV preset in the list syntax the request accepts. */
export const COMMON_AV_PORTS_SPEC = COMMON_AV_PORTS.map((p) => p.port).join(',');

/** The usual service for a port, or null when it is not a known AV port. */
export function describePort(port: number): string | null {
    return COMMON_AV_PORTS.find((p) => p.port === port)?.service ?? null;
}

/** The most packets that fit in the total allowance at the given timeout. */
export function maxPingCountFor(timeoutMs: number): number {
    if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) return PING_LIMITS.maxCount;
    const affordable = Math.floor(PING_LIMITS.maxTotalPingMs / timeoutMs);
    return Math.min(PING_LIMITS.maxCount, Math.max(PING_LIMITS.minCount, affordable));
}
